/**
 * AASB 137 — Provisions, Contingent Liabilities and Contingent Assets
 * Rulepack v1.0.0. Validates recognition criteria + movement schedule + discounting flag.
 *
 * Out of scope: present-value remeasurement using a specific discount rate (flagged when
 * settlement > 12 months but not recalculated); onerous-contract economic-cost recalculation;
 * restructuring detailed-plan tests.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseDate, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const RULEPACK_VERSION = 'aasb137-1.0.0';
const fb = findingBuilder('AASB_137', RULEPACK_VERSION);

const HMAP = makeHeaderMap([
  ['provision_id', 'provision id', 'id', 'no'],
  ['provision_type', 'provision type', 'type', 'category'],
  ['recognition_criteria_met', 'recognition criteria', 'criteria met', 'criteria', 'recognition criteria met'],
  ['best_estimate_amount', 'best estimate', 'best estimate amount', 'amount', 'estimate'],
  ['expected_settlement_date', 'expected settlement', 'settlement date', 'expected settlement date'],
  ['opening_balance', 'opening balance', 'opening', 'b/fwd'],
  ['additions', 'additions', 'increases'],
  ['utilisations', 'utilisations', 'utilisation', 'used', 'paid'],
  ['reversals', 'reversals', 'released'],
  ['closing_balance', 'closing balance', 'closing', 'c/fwd'],
  ['discounted', 'discounted', 'discount applied'],
  ['as_at_date', 'as at', 'as at date'],
]);

const REQUIRED = [
  'provision_id', 'provision_type', 'recognition_criteria_met',
  'best_estimate_amount', 'expected_settlement_date',
  'opening_balance', 'additions', 'utilisations', 'reversals', 'closing_balance',
];

const KNOWN_TYPES = new Set(['onerous_contract', 'onerous contract', 'restructuring', 'warranty', 'legal', 'environmental', 'remediation', 'decommissioning', 'other']);

export function detectAASB137(headerRow: string[]): { confidence: number; matched: string[]; missing: string[] } {
  const hits = new Set<string>();
  for (const raw of headerRow) {
    const c = normaliseHeader(HMAP, String(raw ?? ''));
    if (c) hits.add(c);
  }
  const matched = REQUIRED.filter(r => hits.has(r));
  const missing = REQUIRED.filter(r => !hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing };
}

export function validateAASB137(
  headerRow: string[],
  dataRows: CellValue[][],
  defaultAsAtDate: Date = new Date()
): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];

  const idx: Record<string, number> = {};
  headerRow.forEach((raw, i) => {
    const c = normaliseHeader(HMAP, String(raw ?? ''));
    if (c) idx[c] = i;
  });

  for (const f of REQUIRED) {
    if (idx[f] === undefined) {
      schemaErrors.push(`Missing required column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
    }
  }
  if (schemaErrors.length) {
    return {
      summary: { standard: 'AASB_137', rulepackVersion: RULEPACK_VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() },
      findings, schemaErrors, disclaimer: DISCLAIMER,
    };
  }

  const rowIds: string[] = [];

  dataRows.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('provision_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => {
      const v = get(f);
      return v == null || v === '';
    });
    if (missing.length) {
      findings.push(fb.build('AASB137.R01', 'error', 'Required field missing',
        `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`,
        { rowId: id, evidence: { missing } }));
      return;
    }

    const ptype = String(get('provision_type') ?? '').toLowerCase().replace(/[\s-]+/g, '_');
    const criteriaMet = parseBool(get('recognition_criteria_met'));
    const estimate = parseNumber(get('best_estimate_amount'));
    const settleD = parseDate(get('expected_settlement_date'));
    const opening = parseNumber(get('opening_balance')) ?? 0;
    const additions = parseNumber(get('additions')) ?? 0;
    const utilisations = parseNumber(get('utilisations')) ?? 0;
    const reversals = parseNumber(get('reversals')) ?? 0;
    const closing = parseNumber(get('closing_balance'));
    const asAt = idx.as_at_date !== undefined ? (parseDate(get('as_at_date')) ?? defaultAsAtDate) : defaultAsAtDate;
    const discounted = idx.discounted !== undefined ? parseBool(get('discounted')) : null;

    // R02 type recognised
    if (!KNOWN_TYPES.has(ptype)) {
      findings.push(fb.build('AASB137.R02', 'info', 'Provision type not in standard taxonomy',
        `Provision ${id}: type "${ptype}" — confirm. Common types: onerous_contract, restructuring, warranty, legal, environmental.`,
        { rowId: id, evidence: { ptype } }));
    }

    // R03 recognition criteria for any positive balance
    if ((closing ?? 0) > 0 && !criteriaMet) {
      findings.push(fb.build('AASB137.R03', 'error', 'Provision recognised without recognition criteria met',
        `Provision ${id}: closing balance $${fmt(closing ?? 0)} > 0 but recognition_criteria_met = false. AASB 137.14 requires (a) present obligation, (b) probable outflow, (c) reliable estimate. If any is not met, this is a contingent liability — should be disclosed, not recognised.`,
        { rowId: id, evidence: { closing, criteriaMet } }));
    }

    // R04 best estimate >= 0
    if (estimate !== null && estimate < 0) {
      findings.push(fb.build('AASB137.R04', 'error', 'Best-estimate amount cannot be negative',
        `Provision ${id}: best estimate $${fmt(estimate)}.`,
        { rowId: id }));
    }

    // R05 movement schedule reconciliation
    if (closing !== null) {
      const expectedClosing = opening + additions - utilisations - reversals;
      const tol = Math.max(50, 0.005 * Math.max(Math.abs(opening), Math.abs(closing), 1000));
      const delta = closing - expectedClosing;
      if (Math.abs(delta) > tol) {
        findings.push(fb.build('AASB137.R05', 'error', 'Movement schedule does not reconcile',
          `Provision ${id}: opening $${fmt(opening)} + additions $${fmt(additions)} − utilisations $${fmt(utilisations)} − reversals $${fmt(reversals)} = $${fmt(expectedClosing)}, but reported closing $${fmt(closing)} (delta ${delta >= 0 ? '+' : '−'}$${fmt(Math.abs(delta))}).`,
          {
            rowId: id,
            evidence: { opening, additions, utilisations, reversals, expectedClosing, reportedClosing: closing, delta },
            suggestedAdjustment: { field: 'closing_balance', from: closing, to: +expectedClosing.toFixed(2), rationale: 'Movement schedule arithmetic.' },
          }));
      }
    }

    // R06 discounting required for >12 month settlements
    if (settleD && asAt) {
      const monthsToSettle = (settleD.getFullYear() - asAt.getFullYear()) * 12 + (settleD.getMonth() - asAt.getMonth());
      if (monthsToSettle > 12 && discounted === false) {
        findings.push(fb.build('AASB137.R06', 'warning', 'Long-dated provision not discounted',
          `Provision ${id}: settles in ${monthsToSettle} months but discounted flag is false. AASB 137.45 requires the provision to be the present value of the expenditures expected when material.`,
          { rowId: id, evidence: { monthsToSettle } }));
      }
    }

    // R07 settlement date past — should have been utilised
    if (settleD && settleD < asAt && (closing ?? 0) > 0 && utilisations === 0) {
      findings.push(fb.build('AASB137.R07', 'warning', 'Expected settlement date passed with closing balance > 0 and no utilisation',
        `Provision ${id}: expected settlement ${settleD.toISOString().slice(0, 10)} has passed but closing balance $${fmt(closing ?? 0)} remains. Reassess.`,
        { rowId: id, evidence: { settleD: settleD.toISOString(), closing } }));
    }

    // R08 utilisations + reversals can't exceed opening + additions
    if (utilisations + reversals > opening + additions + 50) {
      findings.push(fb.build('AASB137.R08', 'error', 'Utilisations + reversals exceed available provision',
        `Provision ${id}: utilisations $${fmt(utilisations)} + reversals $${fmt(reversals)} > opening $${fmt(opening)} + additions $${fmt(additions)}.`,
        { rowId: id }));
    }

    // R09 contingent liability flagged as provision
    if (!criteriaMet && (closing ?? 0) === 0) {
      findings.push(fb.build('AASB137.R09', 'info', 'Contingent liability — disclose, do not recognise',
        `Provision ${id}: criteria not met and closing balance $0 — appropriate. Confirm disclosure in notes per AASB 137.86.`,
        { rowId: id }));
    }
  });

  return {
    summary: {
      standard: 'AASB_137',
      rulepackVersion: RULEPACK_VERSION,
      rowsTested: rowIds.length,
      compliant: compliantCount(rowIds, findings),
      byseverity: severityCounts(findings),
      runAt: new Date().toISOString(),
    },
    findings,
    disclaimer: DISCLAIMER,
  };
}
